import { useEffect, useMemo, useState } from 'react'
import { Icon } from '@/components/icons'
import { Button, Input } from '@/components/ui'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export type WorkerOption = {
  id: string
  name: string
  phone?: string | null
  position?: string | null
}

type WorkerNameComboboxProps = {
  id: string
  workers: WorkerOption[]
  value: string
  onChange: (name: string) => void
  placeholder: string
  searchPlaceholder: string
  fromListHeading: string
  listEmptyMessage: string
  /** e.g. (name) => `ใช้ชื่อ "${name}"` */
  formatCustomNameLabel: (name: string) => string
  /** Larger touch targets for LIFF / mobile */
  size?: 'default' | 'large'
}

export function WorkerNameCombobox({
  id,
  workers,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  fromListHeading,
  listEmptyMessage,
  formatCustomNameLabel,
  size = 'default',
}: WorkerNameComboboxProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const isLarge = size === 'large'

  useEffect(() => {
    if (open) setSearch(value)
  }, [open, value])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return workers
    return workers.filter(
      (w) =>
        w.name.toLowerCase().includes(q) ||
        (w.phone && w.phone.toLowerCase().includes(q)) ||
        (w.position && w.position.toLowerCase().includes(q)),
    )
  }, [workers, search])

  const qTrim = search.trim()
  const qNorm = qTrim.toLowerCase()
  const hasExactName = workers.some((w) => w.name.toLowerCase() === qNorm)
  const showUseCustom = qTrim.length > 0 && !hasExactName

  const selectWorker = (name: string) => {
    onChange(name)
    setSearch(name)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-full justify-between rounded-md border border-b-primary bg-s-input px-3 font-normal text-foreground shadow-sm hover:bg-s-input',
            isLarge ? 'h-12 text-base' : 'h-11 text-sm',
            !value && 'text-muted-foreground',
          )}
        >
          <span className="truncate text-left">{value || placeholder}</span>
          <Icon
            icon="lucide:chevrons-up-down"
            className={cn('ml-2 shrink-0 opacity-50', isLarge ? 'size-5' : 'size-4')}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex items-center border-b border-border/60 px-3">
          <Icon
            icon="lucide:search"
            className={cn('mr-2 shrink-0 opacity-50', isLarge ? 'size-5' : 'size-4')}
          />
          <Input
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={cn(
              'border-0 bg-transparent px-0 shadow-none focus-visible:ring-0',
              isLarge ? 'h-12 text-base' : 'h-11 text-sm',
            )}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                if (filtered.length === 1) {
                  selectWorker(filtered[0]!.name)
                } else if (showUseCustom) {
                  selectWorker(qTrim)
                }
              }
            }}
          />
        </div>
        <div
          className={cn('overflow-y-auto p-1', isLarge ? 'max-h-[min(50vh,320px)]' : 'max-h-[300px]')}
        >
          {filtered.length === 0 && !showUseCustom ? (
            <p
              className={cn(
                'px-3 py-6 text-center text-muted-foreground',
                isLarge ? 'text-base' : 'text-sm',
              )}
            >
              {listEmptyMessage}
            </p>
          ) : null}
          {filtered.length > 0 ? (
            <div role="listbox" aria-label={fromListHeading}>
              <p
                className={cn(
                  'px-2 py-1.5 font-medium text-muted-foreground',
                  isLarge ? 'text-sm' : 'text-xs',
                )}
              >
                {fromListHeading}
              </p>
              {filtered.map((w) => (
                <button
                  key={w.id}
                  type="button"
                  role="option"
                  className={cn(
                    'flex w-full cursor-pointer flex-col gap-0.5 rounded-sm px-2 text-left outline-none',
                    'hover:bg-accent hover:text-accent-foreground',
                    value === w.name && 'bg-accent text-accent-foreground',
                    isLarge ? 'py-3 text-base' : 'py-2 text-sm',
                  )}
                  onClick={() => selectWorker(w.name)}
                >
                  <span className="font-medium">{w.name}</span>
                  {w.position || w.phone ? (
                    <span
                      className={cn('text-muted-foreground', isLarge ? 'text-sm' : 'text-xs')}
                    >
                      {[w.position, w.phone].filter(Boolean).join(' · ')}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
          ) : null}
          {showUseCustom ? (
            <button
              type="button"
              className={cn(
                'flex w-full cursor-pointer rounded-sm px-2 text-left hover:bg-accent hover:text-accent-foreground',
                isLarge ? 'py-3 text-base' : 'py-2 text-sm',
              )}
              onClick={() => selectWorker(qTrim)}
            >
              {formatCustomNameLabel(qTrim)}
            </button>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  )
}
